import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssetPreview } from "../../components/AssetPreview";
import { useTenantAuth } from "../../context/auth";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api";

type Staff = {
  id: string;
  full_name: string;
  branch_id: string;
  skill_tags: string[];
  is_active: boolean;
  photo_asset_id?: string | null;
};

type UploadOut = { id: string };
type Asset = { id: string; title?: string | null; original_filename: string };
type WorkingHourRow = { id: string; staff_id: string; weekday: number; start_time: string; end_time: string };
type TimeOffRow = { id: string; staff_id: string; start_at: string; end_at: string; reason?: string | null };
type DraftRange = { key: string; sourceId?: string; start_time: string; end_time: string };
type WeekDraft = Record<number, DraftRange[]>;

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
let draftRangeCounter = 0;

function nextDraftKey() {
  draftRangeCounter += 1;
  return `draft-${draftRangeCounter}`;
}

function normalizeTimeInput(value: string) {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(value || "").trim());
  if (!match) return "";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return "";
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return "";
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function timeToMinutes(value: string) {
  const norm = normalizeTimeInput(value);
  if (!norm) return null;
  const [h, m] = norm.split(":").map(Number);
  return h * 60 + m;
}

function emptyWeekDraft(): WeekDraft {
  return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
}

function cloneWeekDraft(draft: WeekDraft): WeekDraft {
  const out = emptyWeekDraft();
  for (let i = 0; i < 7; i += 1) out[i] = (draft[i] ?? []).map((row) => ({ ...row }));
  return out;
}

function buildWeekDraft(rows: WorkingHourRow[]): WeekDraft {
  const out = emptyWeekDraft();
  [...rows]
    .sort((a, b) => a.weekday - b.weekday || a.start_time.localeCompare(b.start_time) || a.end_time.localeCompare(b.end_time))
    .forEach((row) => {
      if (row.weekday < 0 || row.weekday > 6) return;
      out[row.weekday].push({
        key: nextDraftKey(),
        sourceId: row.id,
        start_time: normalizeTimeInput(row.start_time) || row.start_time.slice(0, 5),
        end_time: normalizeTimeInput(row.end_time) || row.end_time.slice(0, 5),
      });
    });
  return out;
}

function workingHourSignature(row: { weekday: number; start_time: string; end_time: string }) {
  return `${row.weekday}|${normalizeTimeInput(row.start_time)}|${normalizeTimeInput(row.end_time)}`;
}

function draftSignatures(draft: WeekDraft) {
  const out: string[] = [];
  for (let weekday = 0; weekday < 7; weekday += 1) {
    for (const row of draft[weekday] ?? []) {
      const start = normalizeTimeInput(row.start_time);
      const end = normalizeTimeInput(row.end_time);
      if (!start || !end) continue;
      out.push(`${weekday}|${start}|${end}`);
    }
  }
  return out.sort();
}

function existingSignatures(rows: WorkingHourRow[]) {
  return rows.map(workingHourSignature).sort();
}

function sameScheduleDraft(draft: WeekDraft, rows: WorkingHourRow[]) {
  const a = draftSignatures(draft);
  const b = existingSignatures(rows);
  if (a.length !== b.length) return false;
  return a.every((sig, i) => sig === b[i]);
}

function validateWeekDraft(draft: WeekDraft) {
  const errors: string[] = [];
  for (let weekday = 0; weekday < 7; weekday += 1) {
    const rows = (draft[weekday] ?? []).map((row) => ({
      ...row,
      start_time: normalizeTimeInput(row.start_time),
      end_time: normalizeTimeInput(row.end_time),
    }));
    rows.forEach((row, idx) => {
      const startMin = timeToMinutes(row.start_time);
      const endMin = timeToMinutes(row.end_time);
      if (startMin == null || endMin == null) {
        errors.push(`${WEEKDAY_LABELS[weekday]} row ${idx + 1}: invalid time format`);
        return;
      }
      if (endMin <= startMin) errors.push(`${WEEKDAY_LABELS[weekday]} row ${idx + 1}: end must be later than start`);
    });
    const validRows = rows
      .map((row, idx) => ({ row, idx, start: timeToMinutes(row.start_time), end: timeToMinutes(row.end_time) }))
      .filter((x) => x.start != null && x.end != null && x.end > x.start)
      .sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
    for (let i = 1; i < validRows.length; i += 1) {
      const prev = validRows[i - 1];
      const curr = validRows[i];
      if ((curr.start ?? 0) < (prev.end ?? 0)) errors.push(`${WEEKDAY_LABELS[weekday]} rows ${prev.idx + 1} and ${curr.idx + 1}: overlapping ranges`);
    }
    const seen = new Set<string>();
    validRows.forEach(({ row, idx }) => {
      const sig = `${row.start_time}|${row.end_time}`;
      if (seen.has(sig)) errors.push(`${WEEKDAY_LABELS[weekday]} row ${idx + 1}: duplicate range`);
      seen.add(sig);
    });
  }
  return errors;
}

function toIsoFromLocalDateTime(value: string) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

function timeOffInputError(startLocal: string, endLocal: string) {
  if (!startLocal || !endLocal) return "Start and end are required.";
  const start = new Date(startLocal).getTime();
  const end = new Date(endLocal).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "Invalid date or time.";
  if (end <= start) return "End must be later than start.";
  return "";
}

function fmtDateTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString([], { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function draftRow(start_time = "09:00", end_time = "17:00"): DraftRange {
  return { key: nextDraftKey(), start_time, end_time };
}

function daySummaryText(rows: DraftRange[]) {
  if (!rows.length) return "Closed";
  if (rows.length === 1) return "1 window";
  return `${rows.length} windows`;
}

export function ManagerStaffPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { bootstrap, token } = useTenantAuth();
  const [branchId, setBranchId] = useState(bootstrap?.branches[0]?.id ?? "");
  const [name, setName] = useState("");
  const [skills, setSkills] = useState("cuts,color");
  const [photoAssetId, setPhotoAssetId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  const [editName, setEditName] = useState("");
  const [editSkills, setEditSkills] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editPhotoAssetId, setEditPhotoAssetId] = useState("");
  const [uploadingFor, setUploadingFor] = useState("");

  const list = useQuery({
    queryKey: ["manager-staff"],
    queryFn: () => apiGet<{ items: Staff[] }>("/v3/staff"),
  });
  const assets = useQuery({
    queryKey: ["manager-staff-assets"],
    queryFn: () => apiGet<{ items: Asset[] }>("/v3/branding/assets?deleted=false&type=staff"),
  });

  async function uploadStaffPhoto(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "staff");
    const res = await fetch("/v3/branding/assets", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) throw new Error("Asset upload failed");
    const data = (await res.json()) as UploadOut;
    await qc.invalidateQueries({ queryKey: ["manager-staff-assets"] });
    return data.id;
  }

  const create = useMutation({
    mutationFn: () =>
      apiPost("/v3/staff", {
        branch_id: branchId,
        full_name: name,
        skill_tags: skills.split(",").map((x) => x.trim()).filter(Boolean),
        photo_asset_id: photoAssetId || null,
        is_active: true,
      }),
    onSuccess: async () => {
      setName("");
      setPhotoAssetId("");
      await qc.invalidateQueries({ queryKey: ["manager-staff"] });
    },
  });

  const update = useMutation({
    mutationFn: () =>
      apiPut(`/v3/staff/${editingId}`, {
        branch_id: editBranchId,
        full_name: editName,
        skill_tags: editSkills.split(",").map((x) => x.trim()).filter(Boolean),
        photo_asset_id: editPhotoAssetId || null,
        is_active: editActive,
      }),
    onSuccess: async () => {
      setEditingId("");
      await qc.invalidateQueries({ queryKey: ["manager-staff"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/v3/staff/${id}`),
    onSuccess: async () => {
      if (editingId) setEditingId("");
      await qc.invalidateQueries({ queryKey: ["manager-staff"] });
    },
  });

  const beginEdit = (row: Staff) => {
    setEditingId(row.id);
    setEditBranchId(row.branch_id);
    setEditName(row.full_name);
    setEditSkills(row.skill_tags.join(","));
    setEditActive(row.is_active);
    setEditPhotoAssetId(row.photo_asset_id || "");
  };

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">{t("manager.staff.title")}</h1>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.staff.team")}</h2>
        <div className="mt-3 grid gap-2">
          {(list.data?.items ?? []).map((row) => {
            const branchName = bootstrap?.branches.find((b) => b.id === row.branch_id)?.name || t("manager.staff.unknownBranch");
            return (
              <article key={row.id} className="rounded-lg border border-slate-200 bg-white p-3">
                {editingId === row.id ? (
                  <div className="grid gap-3">
                    <div className="grid gap-2">
                      <select className="input" value={editBranchId} onChange={(e) => setEditBranchId(e.target.value)}>
                        {(bootstrap?.branches ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                      <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                      <input className="input" value={editSkills} onChange={(e) => setEditSkills(e.target.value)} />
                      <div className="flex flex-wrap items-center gap-2">
                        {editPhotoAssetId ? <AssetPreview assetId={editPhotoAssetId} token={token} className="h-12 w-12 rounded border object-cover" alt={t("staff")} /> : null}
                        <select className="input min-w-0 flex-1" value={editPhotoAssetId} onChange={(e) => setEditPhotoAssetId(e.target.value)}>
                          <option value="">{t("manager.media.selectFromLibrary")}</option>
                          {(assets.data?.items ?? []).map((asset) => <option key={asset.id} value={asset.id}>{asset.title || asset.original_filename}</option>)}
                        </select>
                        <input
                          className="input w-full sm:w-auto"
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploadingFor(row.id);
                            try {
                              const nextId = await uploadStaffPhoto(file);
                              setEditPhotoAssetId(nextId);
                            } finally {
                              setUploadingFor("");
                            }
                          }}
                        />
                        {editPhotoAssetId ? <button type="button" className="btn secondary" onClick={() => setEditPhotoAssetId("")}>{t("manager.media.removePhoto")}</button> : null}
                      </div>
                      <label className="text-sm">
                        <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} /> {t("common.active")}
                      </label>
                    </div>

                    <StaffScheduleEditor staffId={row.id} />

                    <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-2">
                      <button className="btn" type="button" onClick={() => update.mutate()} disabled={!editName.trim() || !editBranchId || uploadingFor === row.id}>
                        Save
                      </button>
                      <button className="btn secondary" type="button" onClick={() => setEditingId("")}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="font-semibold">{row.full_name}</p>
                    <p className="text-sm text-slate-600">{branchName}</p>
                    <p className="text-xs text-slate-500">{t("manager.staff.skillsValue", { value: row.skill_tags.join(", ") || t("manager.staff.none") })}</p>
                    {row.photo_asset_id ? <AssetPreview assetId={row.photo_asset_id} token={token} className="mt-2 h-14 w-14 rounded border object-cover" alt={row.full_name} /> : null}
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button className="btn secondary" type="button" onClick={() => beginEdit(row)}>Edit</button>
                      <button className="btn secondary" type="button" onClick={() => remove.mutate(row.id)}>Delete</button>
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </div>
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.staff.addMember")}</h2>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <select className="input" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {(bootstrap?.branches ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <input className="input" placeholder={t("manager.staff.placeholder.fullName")} value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" placeholder={t("manager.staff.placeholder.skills")} value={skills} onChange={(e) => setSkills(e.target.value)} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {photoAssetId ? <AssetPreview assetId={photoAssetId} token={token} className="h-12 w-12 rounded border object-cover" alt={t("staff")} /> : null}
          <select className="input min-w-0 flex-1" value={photoAssetId} onChange={(e) => setPhotoAssetId(e.target.value)}>
            <option value="">{t("manager.media.selectFromLibrary")}</option>
            {(assets.data?.items ?? []).map((asset) => <option key={asset.id} value={asset.id}>{asset.title || asset.original_filename}</option>)}
          </select>
          <input
            className="input w-full sm:w-auto"
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setUploadingFor("create");
              try {
                const nextId = await uploadStaffPhoto(file);
                setPhotoAssetId(nextId);
              } finally {
                setUploadingFor("");
              }
            }}
          />
          {photoAssetId ? <button type="button" className="btn secondary" onClick={() => setPhotoAssetId("")}>{t("manager.media.removePhoto")}</button> : null}
        </div>
        <button className="btn mt-2 w-full sm:w-auto" type="button" onClick={() => create.mutate()} disabled={!branchId || !name.trim() || uploadingFor === "create"}>
          {t("manager.staff.action.addStaff")}
        </button>
      </section>
    </div>
  );
}

function StaffScheduleEditor({ staffId }: { staffId: string }) {
  const qc = useQueryClient();
  const [weekDraft, setWeekDraft] = useState<WeekDraft>(() => emptyWeekDraft());
  const [scheduleNotice, setScheduleNotice] = useState("");
  const [scheduleError, setScheduleError] = useState("");
  const [timeOffStart, setTimeOffStart] = useState("");
  const [timeOffEnd, setTimeOffEnd] = useState("");
  const [timeOffReason, setTimeOffReason] = useState("");
  const [timeOffError, setTimeOffError] = useState("");
  const [copiedDay, setCopiedDay] = useState<number | null>(null);

  const workingHours = useQuery({
    queryKey: ["manager-staff-working-hours", staffId],
    queryFn: () => apiGet<{ items: WorkingHourRow[] }>(`/v3/staff/${staffId}/working-hours`),
    enabled: !!staffId,
    refetchOnWindowFocus: false,
  });

  const timeOff = useQuery({
    queryKey: ["manager-staff-time-off", staffId],
    queryFn: () => apiGet<{ items: TimeOffRow[] }>(`/v3/staff/${staffId}/time-off`),
    enabled: !!staffId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setScheduleNotice("");
    setScheduleError("");
    setTimeOffError("");
    setCopiedDay(null);
    setTimeOffStart("");
    setTimeOffEnd("");
    setTimeOffReason("");
  }, [staffId]);

  useEffect(() => {
    if (!staffId || !workingHours.data?.items) return;
    setWeekDraft(buildWeekDraft(workingHours.data.items));
  }, [staffId, workingHours.data?.items]);

  const scheduleValidationErrors = useMemo(() => validateWeekDraft(weekDraft), [weekDraft]);
  const scheduleChanged = useMemo(() => !sameScheduleDraft(weekDraft, workingHours.data?.items ?? []), [weekDraft, workingHours.data?.items]);
  const canSaveSchedule = !!staffId && scheduleChanged && scheduleValidationErrors.length === 0 && !workingHours.isLoading;

  const saveSchedule = useMutation({
    mutationFn: async () => {
      const existingRows = workingHours.data?.items ?? [];
      const desired: Array<{ weekday: number; start_time: string; end_time: string }> = [];
      for (let weekday = 0; weekday < 7; weekday += 1) {
        for (const row of weekDraft[weekday] ?? []) {
          const start = normalizeTimeInput(row.start_time);
          const end = normalizeTimeInput(row.end_time);
          if (!start || !end) continue;
          desired.push({ weekday, start_time: start, end_time: end });
        }
      }

      const desiredCounts = new Map<string, number>();
      desired.forEach((row) => {
        const sig = workingHourSignature(row);
        desiredCounts.set(sig, (desiredCounts.get(sig) ?? 0) + 1);
      });

      const existingBySig = new Map<string, WorkingHourRow[]>();
      existingRows.forEach((row) => {
        const sig = workingHourSignature(row);
        existingBySig.set(sig, [...(existingBySig.get(sig) ?? []), row]);
      });

      const deleteIds: string[] = [];
      for (const [sig, rows] of existingBySig.entries()) {
        let keep = desiredCounts.get(sig) ?? 0;
        for (const row of rows) {
          if (keep > 0) {
            keep -= 1;
            continue;
          }
          deleteIds.push(row.id);
        }
      }

      const pool = new Map<string, number>();
      existingRows.forEach((row) => {
        const sig = workingHourSignature(row);
        pool.set(sig, (pool.get(sig) ?? 0) + 1);
      });
      deleteIds.forEach((id) => {
        const found = existingRows.find((r) => r.id === id);
        if (!found) return;
        const sig = workingHourSignature(found);
        pool.set(sig, Math.max(0, (pool.get(sig) ?? 0) - 1));
      });

      const createRows: Array<{ weekday: number; start_time: string; end_time: string }> = [];
      for (const row of desired) {
        const sig = workingHourSignature(row);
        const count = pool.get(sig) ?? 0;
        if (count > 0) {
          pool.set(sig, count - 1);
          continue;
        }
        createRows.push(row);
      }

      for (const id of deleteIds) {
        await apiDelete(`/v3/staff/${staffId}/working-hours/${id}`);
      }
      for (const row of createRows) {
        await apiPost(`/v3/staff/${staffId}/working-hours`, { staff_id: staffId, ...row });
      }
      return { deleted: deleteIds.length, created: createRows.length };
    },
    onSuccess: async (result) => {
      setScheduleNotice(`Schedule saved (${result.created} added, ${result.deleted} removed).`);
      setScheduleError("");
      await qc.invalidateQueries({ queryKey: ["manager-staff-working-hours", staffId] });
      await qc.invalidateQueries({ queryKey: ["manager-calendar-day-layout"] });
    },
    onError: (err) => {
      setScheduleError(err instanceof Error ? err.message : String(err));
      setScheduleNotice("");
    },
  });

  const createTimeOff = useMutation({
    mutationFn: async () => {
      const inputErr = timeOffInputError(timeOffStart, timeOffEnd);
      if (inputErr) throw new Error(inputErr);
      return apiPost(`/v3/staff/${staffId}/time-off`, {
        staff_id: staffId,
        start_at: toIsoFromLocalDateTime(timeOffStart),
        end_at: toIsoFromLocalDateTime(timeOffEnd),
        reason: timeOffReason.trim() || null,
      });
    },
    onSuccess: async () => {
      setTimeOffStart("");
      setTimeOffEnd("");
      setTimeOffReason("");
      setTimeOffError("");
      await qc.invalidateQueries({ queryKey: ["manager-staff-time-off", staffId] });
      await qc.invalidateQueries({ queryKey: ["manager-calendar-day-layout"] });
    },
    onError: (err) => setTimeOffError(err instanceof Error ? err.message : String(err)),
  });

  const deleteTimeOff = useMutation({
    mutationFn: (id: string) => apiDelete(`/v3/staff/${staffId}/time-off/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["manager-staff-time-off", staffId] });
      await qc.invalidateQueries({ queryKey: ["manager-calendar-day-layout"] });
    },
  });

  const setDayRanges = (weekday: number, updater: (rows: DraftRange[]) => DraftRange[]) => {
    setScheduleNotice("");
    setScheduleError("");
    setWeekDraft((prev) => {
      const next = cloneWeekDraft(prev);
      next[weekday] = updater(next[weekday] ?? []);
      return next;
    });
  };

  const addRange = (weekday: number, start = "09:00", end = "17:00") => {
    setDayRanges(weekday, (rows) => [...rows, draftRow(start, end)]);
  };

  const removeRange = (weekday: number, key: string) => {
    setDayRanges(weekday, (rows) => rows.filter((row) => row.key !== key));
  };

  const updateRange = (weekday: number, key: string, patch: Partial<Pick<DraftRange, "start_time" | "end_time">>) => {
    setDayRanges(weekday, (rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const applyTemplate = (kind: "weekdays_9_17" | "daily_10_19" | "clear") => {
    setScheduleNotice("");
    setScheduleError("");
    if (kind === "clear") {
      setWeekDraft(emptyWeekDraft());
      return;
    }
    const next = emptyWeekDraft();
    if (kind === "weekdays_9_17") {
      for (let d = 0; d < 5; d += 1) next[d] = [draftRow("09:00", "17:00")];
    } else {
      for (let d = 0; d < 7; d += 1) next[d] = [draftRow("10:00", "19:00")];
    }
    setWeekDraft(next);
  };

  const copyDayToWeekdays = (sourceDay: number) => {
    const source = (weekDraft[sourceDay] ?? []).map((r) => ({ ...r }));
    const next = cloneWeekDraft(weekDraft);
    for (let d = 0; d < 5; d += 1) next[d] = source.map((r) => ({ ...r, key: nextDraftKey(), sourceId: undefined }));
    setWeekDraft(next);
    setCopiedDay(sourceDay);
    setScheduleNotice(`Copied ${WEEKDAY_LABELS[sourceDay]} schedule to Mon-Fri.`);
    setScheduleError("");
  };

  const copyPreviousDay = (day: number) => {
    if (day <= 0) return;
    const source = weekDraft[day - 1] ?? [];
    setDayRanges(day, () => source.map((r) => ({ ...r, key: nextDraftKey(), sourceId: undefined })));
    setCopiedDay(day - 1);
  };

  const currentTimeOffInputError = timeOffInputError(timeOffStart, timeOffEnd);

  return (
    <section className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Schedule & Time Off</h3>
          <p className="text-xs text-slate-500">Weekly working hours recur every week. Time-off blocks override them. Datetimes use your browser local timezone.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn secondary" onClick={() => applyTemplate("weekdays_9_17")}>Mon-Fri 09:00-17:00</button>
          <button type="button" className="btn secondary" onClick={() => applyTemplate("daily_10_19")}>Daily 10:00-19:00</button>
          <button type="button" className="btn secondary" onClick={() => applyTemplate("clear")}>Clear week</button>
        </div>
      </div>

      <div className="grid gap-3 xl:grid-cols-[1.3fr,1fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">Weekly Working Hours</p>
              <p className="text-xs text-slate-500">Multiple windows per day supported. Save applies changes in bulk.</p>
            </div>
            <button type="button" className="btn secondary" onClick={() => copyDayToWeekdays(0)} disabled={!weekDraft[0]?.length}>Copy Mon to Mon-Fri</button>
          </div>

          {workingHours.isLoading ? <p className="mt-2 text-sm text-slate-500">Loading schedule...</p> : null}
          {workingHours.error ? <p className="mt-2 text-sm text-red-600">{String(workingHours.error)}</p> : null}

          <div className="mt-3 grid gap-2">
            {Array.from({ length: 7 }, (_, weekday) => {
              const rows = weekDraft[weekday] ?? [];
              return (
                <div key={weekday} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{WEEKDAY_LABELS[weekday]}</p>
                      <p className="text-xs text-slate-500">{daySummaryText(rows)}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {weekday > 0 ? <button type="button" className="btn secondary" onClick={() => copyPreviousDay(weekday)}>Copy {WEEKDAY_LABELS[weekday - 1]}</button> : null}
                      <button type="button" className="btn secondary" onClick={() => addRange(weekday)}>+ Add window</button>
                    </div>
                  </div>

                  {!rows.length ? <p className="mt-2 text-xs text-slate-500">No working windows for this day.</p> : null}
                  <div className="mt-2 grid gap-2">
                    {rows.map((row, idx) => (
                      <div key={row.key} className="grid items-center gap-2 sm:grid-cols-[auto,1fr,1fr,auto]">
                        <span className="text-xs text-slate-500">#{idx + 1}</span>
                        <input type="time" className="input" value={row.start_time} onChange={(e) => updateRange(weekday, row.key, { start_time: e.target.value })} />
                        <input type="time" className="input" value={row.end_time} onChange={(e) => updateRange(weekday, row.key, { end_time: e.target.value })} />
                        <button type="button" className="btn secondary" onClick={() => removeRange(weekday, row.key)}>Remove</button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {scheduleValidationErrors.length ? (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">
              <p className="font-medium">Fix schedule issues before saving:</p>
              <ul className="mt-1 list-disc pl-5">
                {scheduleValidationErrors.slice(0, 6).map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
              {scheduleValidationErrors.length > 6 ? <p className="mt-1 text-xs">+{scheduleValidationErrors.length - 6} more</p> : null}
            </div>
          ) : null}

          {scheduleError ? <p className="mt-2 text-sm text-red-600">{scheduleError}</p> : null}
          {scheduleNotice ? <p className="mt-2 text-sm text-emerald-700">{scheduleNotice}</p> : null}
          {copiedDay != null ? <p className="mt-1 text-xs text-slate-500">Last copy source: {WEEKDAY_LABELS[copiedDay]}</p> : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn" disabled={!canSaveSchedule || saveSchedule.isPending} onClick={() => saveSchedule.mutate()}>
              {saveSchedule.isPending ? "Saving schedule..." : "Save Schedule"}
            </button>
            <button
              type="button"
              className="btn secondary"
              disabled={workingHours.isLoading || saveSchedule.isPending}
              onClick={() => {
                setScheduleError("");
                setScheduleNotice("");
                setWeekDraft(buildWeekDraft(workingHours.data?.items ?? []));
              }}
            >
              Reset Draft
            </button>
            <span className="self-center text-xs text-slate-500">{scheduleChanged ? "Unsaved changes" : "No changes"}</span>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-sm font-semibold">Time Off / Exceptions</p>
          <p className="text-xs text-slate-500">Add vacations, sick leave, training, or one-off unavailable blocks.</p>

          <div className="mt-3 grid gap-2">
            <div>
              <label className="text-xs text-slate-500">Start</label>
              <input className="input mt-1" type="datetime-local" value={timeOffStart} onChange={(e) => setTimeOffStart(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500">End</label>
              <input className="input mt-1" type="datetime-local" value={timeOffEnd} onChange={(e) => setTimeOffEnd(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Reason (optional)</label>
              <input className="input mt-1" value={timeOffReason} onChange={(e) => setTimeOffReason(e.target.value)} placeholder="Vacation, training, break..." />
            </div>
          </div>

          {timeOffError ? <p className="mt-2 text-sm text-red-600">{timeOffError}</p> : null}
          {!timeOffError && currentTimeOffInputError && (timeOffStart || timeOffEnd) ? <p className="mt-2 text-xs text-amber-700">{currentTimeOffInputError}</p> : null}

          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn" disabled={createTimeOff.isPending || !!currentTimeOffInputError} onClick={() => createTimeOff.mutate()}>
              {createTimeOff.isPending ? "Adding..." : "Add Time Off"}
            </button>
            <button
              type="button"
              className="btn secondary"
              onClick={() => {
                setTimeOffError("");
                setTimeOffReason("");
                setTimeOffStart("");
                setTimeOffEnd("");
              }}
            >
              Clear
            </button>
          </div>

          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">Existing Time Off</p>
              <button type="button" className="btn secondary" onClick={() => timeOff.refetch()} disabled={timeOff.isFetching}>Refresh</button>
            </div>
            {timeOff.isLoading ? <p className="mt-2 text-sm text-slate-500">Loading time off...</p> : null}
            {timeOff.error ? <p className="mt-2 text-sm text-red-600">{String(timeOff.error)}</p> : null}
            <div className="mt-2 grid gap-2">
              {(timeOff.data?.items ?? []).map((row) => (
                <div key={row.id} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                  <p className="text-sm font-medium text-slate-800">{fmtDateTime(row.start_at)} - {fmtDateTime(row.end_at)}</p>
                  <p className="text-xs text-slate-500">{row.reason?.trim() || "No reason"}</p>
                  <div className="mt-2">
                    <button type="button" className="btn secondary" disabled={deleteTimeOff.isPending} onClick={() => deleteTimeOff.mutate(row.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {!timeOff.isLoading && !(timeOff.data?.items?.length ?? 0) ? <p className="text-xs text-slate-500">No time-off entries.</p> : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
